// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Chart2.js
// Chart 2 (/chart2): woody.wtf's own chart engine (frontend/src/chart/), built alongside the
// Highcharts page (/chart) until it matches it. Phase 1: candles, volume, crosshair,
// OHLCV readout and gestures (pan, pinch/wheel zoom, price-axis stretch, double-tap reset).
// Phase 2: toolbar (desktop rail / phone bottom bar, groups in GROUPS below), timeframes
// (D/W/M/6M/Y built from daily candles), reset / fit all / full screen.
// Phase 3: drawing tools (chart/drawings.js) saved per user + ticker via /api/drawings
// (backend/drawings.py, table public.chart_drawings); Save button or auto-save (option).
// Ray tool, line/fill opacity, multi-select (Ctrl/Shift+click or "Select several"), fib level
// settings panel, multi-line text dialog.
// Indicators (chart/indicators.js, calculated in the browser from the candles shown): picker,
// legend with values at the crosshair, settings, own panes; favourites + default layout saved
// per user via /api/users/chart-prefs (table public.user_chart_prefs), along with each drawing
// tool's last-used settings (prefs.drawStyles) so new drawings start the way the user left them.
// Alert lines (Alerts group): an 'alert' drawing + AlertPanel settings, checked after each close by
// woody-jobs alert_lines.py; "My alert lines" lists them across tickers. Fired alerts reach the
// navbar bell inbox (components/AlertBell.js).
// Company strip (chart/InfoBar.js: /chart/info fields, analyst summary, + List, Auto patterns,
// Company page), analyst rating dates on the chart (chart/AnalystMenu.js beside the annotation sets menu: on / off + look settings), desktop date slider.
// Props as the old chart page, so Workspace / Scans can embed it: embedded (no navbar, fills its
// parent), initialTicker, refreshKey, onTickerChange. URL: ?t= (or ?ticker=).
// Same data endpoints as Chart.js: /chart/search, /chart/data, /chart/ratio.
// Commodity futures (backend commodities.py): GC1!:COMEX front month, GC2! second, GCZ2026 one contract;
// toolbar toggles as TradingView: ADJ back-adjusted, SET settlement as close, EXT long IBKR history.
// Live candle: today's candle so far from /chart/live (market.live_bars - US via moomoo, real-time; ASX
// 20 min delayed) after the last confirmed bar, purple until the EOD bar arrives (black/white in US
// pre-market, after-hours price as a marker). Refreshed every minute while visible; ASX charts also
// fetch from asx.com.au in the browser (chart/asxLive.js) every 3 minutes while the chart has focus.
// Market state (PRE / OPEN / POST / CLOSED) sits before the ticker symbol; the OHLCV readout has its
// own row above the chart so nothing overlays the latest candles.
// Intraday (Oct 2026): 1m-4h from /chart/intraday (US, rolling month of 1-minute candles, New York
// time), built in the browser like W/M/Y; EXT shows / hides pre-market and after-hours (shaded).
// Timeframe picker (chart/TimeframePicker.js): favourites row + grouped menu, phone chip + sheet;
// favourites and EXT saved in the user's chart prefs (tfFav, tfExt).
// Annotation sets (chart/SetsMenu.js, backend drawings.py): named sets per chart, several shown at
// once; new drawings go into the active set, the other shown sets are read-only overlays. Sets are
// Private / Public / Share; others' sets load read-only, "Edit copy" makes a set of your own. Each set
// holds daily drawings (D-Y, anchored to daily dates) and intraday ones (1m-4h, anchored to minutes):
// switching between the two groups swaps which drawings show. Alert lines sit outside sets (daily only).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ChartPatternSettings from './ChartPatternSettings';
import { readPatternSettings, normaliseSettings, detectPatterns, PATTERN_TYPES } from './chartPatterns';
import InfoBar from '../chart/InfoBar';
import AddToListModal from '../chart/AddToList';
import Navigator from '../chart/Navigator';
import { RotateCcw, ArrowLeftRight, Maximize, Minimize, Pencil, Bell, Activity } from 'lucide-react';
import ChartEngine from '../chart/ChartEngine';
import Toolbar from '../chart/Toolbar';
import { aggregate, isIntraday, tfInfo, DEFAULT_FAV_TF } from '../chart/timeframes';
import { nyParts, nySession } from '../chart/ChartEngine';
import TimeframePicker from '../chart/TimeframePicker';
import SetsMenu from '../chart/SetsMenu';
import AnalystMenu, { analystStyle } from '../chart/AnalystMenu';
import { asxToday } from '../chart/asxLive';
import DrawingEditor, { TOOLS, hasDrawingClipboard } from '../chart/drawings';
import DrawingBar from '../chart/DrawingBar';
import FibPanel from '../chart/FibPanel';
import TextDialog from '../chart/TextDialog';
import IndicatorPicker from '../chart/IndicatorPicker';
import AlertPanel from '../chart/AlertPanel';
import MyAlerts from '../chart/MyAlerts';
import IndicatorSettings from '../chart/IndicatorSettings';
import { INDICATORS, newStudy, studyLabel } from '../chart/indicators';
import { IconArc, IconBuy, IconSell, IconArrow, IconRay, IconSelect, IconTrend, IconChannel, IconHLine, IconHRay, IconVLine, IconRect, IconCircle, IconEllipse, IconFib, IconMeasure, IconText, IconCallout, IconBubble, IconMagnet, IconTrash, IconSave, IconAuto } from '../chart/icons';

const SEARCH_DEBOUNCE = 300;
const AUTO_SAVE_DELAY = 1500;
const DRAW_TOOLS = [
  ['trend', IconTrend], ['channel', IconChannel], ['arrow', IconArrow], ['ray', IconRay], ['arc', IconArc], ['hline', IconHLine], ['hray', IconHRay], ['vline', IconVLine],
  ['rect', IconRect], ['circle', IconCircle], ['ellipse', IconEllipse],
  ['fib', IconFib], ['measure', IconMeasure], ['text', IconText], ['callout', IconCallout], ['bubble', IconBubble], ['buy', IconBuy], ['sell', IconSell],
];
const FUT_VENUES = ['COMEX', 'NYMEX', 'CBOT', 'CME', 'ICEUS'];   // market.commodities.venue
const isFutTicker = (t) => (t || '').split('/').some(x => FUT_VENUES.includes((x.split(':')[1] || '').trim().toUpperCase()));
const LIVE_EVERY = 60000;        // server live candle refresh while the page is visible
const ASX_EVERY = 180000;        // browser asx.com.au fetch while this chart has focus
let activeChart = null;          // Workspace: the embedded chart last touched gets the ASX browser fetch
const MKT_STATE = { pre: 'PRE', post: 'POST', closed: 'CLOSED' };   // live session -> tag in the ticker box (else OPEN)
const mktState = (session) => MKT_STATE[session] || 'OPEN';
const readPref = (k, dflt) => { try { const v = localStorage.getItem(k); return v === null ? dflt : v === 'true'; } catch (_) { return dflt; } };
const writePref = (k, v) => { try { localStorage.setItem(k, String(v)); } catch (_) {} };
const scopeOf = id => (isIntraday(id) ? 'intraday' : 'daily');
const intradayOk = t => /:US$/i.test(t || '') && !t.includes('/') && !isFutTicker(t);
// Active / shown annotation sets, remembered per user + chart in this browser.
const setPrefKey = (uid, t) => `chart2Sets:${uid || 'guest'}:${t}`;
const readSetPref = (uid, t) => { try { return JSON.parse(localStorage.getItem(setPrefKey(uid, t))) || null; } catch (_) { return null; } };
const writeSetPref = (uid, t, v) => { try { localStorage.setItem(setPrefKey(uid, t), JSON.stringify(v)); } catch (_) {} };
const pad2 = n => String(n).padStart(2, '0');
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Drawing as sent to /drawings/save.
const serialise = d => ({ id: d.id || null, key: d.key, type: d.type, scope: d.scope || 'daily',
                          data: { pts: d.pts, pane: d.pane, style: d.style, text: d.text, locked: !!d.locked, fib: d.fib, alert: d.alert } });

const S = {
  page:       { height: '100vh', overflow: 'hidden', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  controls:   { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, minHeight: '52px' },
  searchWrap: { position: 'relative', flex: '0 0 300px' },
  searchInput:{ width: '100%', padding: '7px 12px', fontSize: '13px', fontFamily: "'JetBrains Mono',monospace", border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none', letterSpacing: '0.05em', boxSizing: 'border-box' },
  dropdown:   { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', zIndex: 100, maxHeight: '280px', overflowY: 'auto' },
  dropItem:   { padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', borderBottom: '1px solid var(--border)' },
  dropSymbol: { fontFamily: "'JetBrains Mono',monospace", fontWeight: 500, color: 'var(--text)', flexShrink: 0 },
  dropName:   { fontSize: '12px', color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge:      { fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: '3px', fontWeight: 600, flexShrink: 0 },
  tfWrap:     { display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', flexShrink: 0 },
  tfBar:      { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  tfBtn:      { padding: '5px 10px', fontSize: '12px', fontFamily: "'JetBrains Mono',monospace", border: 'none', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer' },
  chartRow:   { flex: 1, display: 'flex', minHeight: 0 },
  name:       { fontSize: '12px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  chartWrap:  { flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden', background: 'var(--surface)' },
  host:       { position: 'absolute', inset: 0 },
  contextMenu: { position: 'absolute', zIndex: 25, width: '158px', padding: '4px', background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' },
  contextItem: { width: '100%', padding: '7px 8px', border: 0, borderRadius: '3px', background: 'transparent', color: 'var(--text)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontSize: '12px', textAlign: 'left' },
  topLeft:    { position: 'absolute', top: '6px', left: '8px', right: '72px', zIndex: 5, pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' },
  readout:    { display: 'flex', flexWrap: 'nowrap', gap: '2px 10px', alignItems: 'baseline', whiteSpace: 'nowrap', flexShrink: 0 },
  readoutBar: { flexShrink: 0, height: '22px', display: 'flex', alignItems: 'center', padding: '0 8px', overflowX: 'auto', scrollbarWidth: 'none', background: 'var(--surface)', borderBottom: '1px solid var(--border)' },
  legRow:     { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--surface)', opacity: 0.94, borderRadius: '4px', padding: '1px 4px', pointerEvents: 'auto', fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", maxWidth: '100%', flexWrap: 'wrap' },
  legName:    { border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, color: 'var(--text)' },
  legBtn:     { border: 'none', background: 'none', padding: '0 2px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1 },
  paneLeg:    { position: 'absolute', left: '8px', zIndex: 5 },
  pill:       { display: 'inline-flex', gap: '3px', alignItems: 'baseline', background: 'var(--surface)', opacity: 0.92, borderRadius: '3px', padding: '0 3px', fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', boxSizing: 'border-box', minWidth: 0 },
  pillLabel:  { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace" },
  pillValue:  { fontSize: '11px', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace", fontVariantNumeric: 'tabular-nums' },
  resetBtn:   { position: 'absolute', right: '72px', bottom: '32px', zIndex: 5, width: '32px', height: '32px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px' },
  overlay:    { position: 'absolute', inset: 0, background: 'var(--veil)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
  spinner:    { width: '32px', height: '32px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'c2spin 0.7s linear infinite' },
  hint:       { position: 'absolute', top: '34px', left: '50%', transform: 'translateX(-50%)', zIndex: 16, display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: '999px', padding: '5px 6px 5px 12px', fontSize: '12px', whiteSpace: 'nowrap', boxShadow: 'var(--shadow)' },
  hintX:      { border: 'none', background: 'rgba(255,255,255,0.25)', color: 'var(--on-accent)', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', fontSize: '12px', lineHeight: '22px', padding: 0 },
  saveStatus: { position: 'absolute', bottom: '32px', left: '8px', zIndex: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600, pointerEvents: 'none' },
  liveTag:    { position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', background: 'var(--live)', color: 'var(--on-accent)', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textDecoration: 'none' },
  error:      { position: 'absolute', top: '40%', left: 0, right: 0, textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', zIndex: 6 },
};

// Phones: search fills the row, smaller readout, bigger reset button for a thumb.
const CSS = `
@keyframes c2spin { to { transform: rotate(360deg); } }
.c2-page { height: 100dvh !important; }
.c2-readout-bar::-webkit-scrollbar { display: none; }
@media (max-width: 768px) {
  .c2-controls { padding: 6px 10px !important; gap: 6px 8px !important; flex-wrap: wrap; }
  .c2-search { flex: 1 1 100% !important; min-width: 0; }
  .c2-tfbar { flex: 1 1 100%; }
  .c2-name { display: none; }
  .c2-reset { width: 40px !important; height: 40px !important; }
  .c2-readout span { font-size: 10px !important; }
  .c2-readout { gap: 4px !important; }
}
`;

// Full precision price, minimum 2dp (same rule as Chart.js).
function fmtPrice(v) {
  if (v == null) return '—';
  const dp = String(v).includes('.') ? String(v).split('.')[1].length : 0;
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: Math.max(2, dp), maximumFractionDigits: Math.max(2, dp) });
}

// Indicator values print with sensible precision (big counts like OBV abbreviated).
function fmtVal(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (a >= 1e4) return (v / 1e3).toFixed(1) + 'K';
  return v.toFixed(a < 1 ? 4 : 2);
}

// One legend row: name (opens settings), values at the crosshair (or last candle), hide, remove.
function StudyRow({ s, idx, onSettings, onToggle, onRemove }) {
  const def = INDICATORS[s.id];
  return (
    <span style={{ ...S.legRow, opacity: s.visible ? 0.94 : 0.5 }}>
      <button style={S.legName} onClick={() => onSettings(s)} title="Settings">{studyLabel(s)}</button>
      {s.visible && def.outputs.filter(o => !s.styles[o.k]?.hidden).map(o => (
        <span key={o.k} style={{ color: o.kind === 'hist' ? 'var(--text-muted)' : s.styles[o.k]?.color }}>{fmtVal(s.out?.[o.k]?.[idx])}</span>
      ))}
      <button style={S.legBtn} onClick={() => onToggle(s)} title={s.visible ? 'Hide' : 'Show'}>{s.visible ? '◉' : '○'}</button>
      <button style={S.legBtn} onClick={() => onRemove(s)} title="Remove">✕</button>
    </span>
  );
}

function Readout({ bar, hasVol }) {
  if (!bar) return null;
  const item = (label, value, width) => (
    <span key={label} style={{ ...S.pill, width: `${width}ch`, flexShrink: 0 }}><span style={S.pillLabel}>{label}</span><span style={S.pillValue}>{value}</span></span>
  );
  const d = new Date(bar.t);
  const when = bar.min != null ? `${bar.dd} ${MON[bar.mo - 1]} ${pad2(Math.floor(bar.min / 60))}:${pad2(bar.min % 60)}`   // New York time
                               : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div className="c2-readout" style={S.readout}>
      {item('', when, 13)}
      {item('O', fmtPrice(bar.o), 12)}
      {item('H', fmtPrice(bar.h), 12)}
      {item('L', fmtPrice(bar.l), 12)}
      {item('C', fmtPrice(bar.c), 12)}
      {hasVol && item('VOL', Number(bar.v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }), 19)}
    </div>
  );
}

export default function Chart2({ embedded = false, initialTicker = null, refreshKey = 0, onTickerChange = null, auditMark = null }) {
  const { user } = useAuth();
  const q = new URLSearchParams(window.location.search);
  const initTicker = initialTicker || q.get('t') || q.get('ticker') || '';   // empty until the user picks one

  const [ticker,        setTicker]        = useState(initTicker);
  const [inputVal,      setInputVal]      = useState(initTicker);
  const [name,          setName]          = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [hovered,       setHovered]       = useState(null);
  const [lastBar,       setLastBar]       = useState(null);
  const [hasVol,        setHasVol]        = useState(false);
  const [tf,            setTf]            = useState('1D');
  const [full,          setFull]          = useState(false);
  const [mobile,        setMobile]        = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [selItems,      setSelItems]      = useState([]);      // selected drawings
  const [contextMenu,   setContextMenu]   = useState(null);    // chart-local right-click menu
  const [textReq,       setTextReq]       = useState(null);    // {initial, done} while the text dialog is open
  const [fibFor,        setFibFor]        = useState(null);    // fib drawing whose settings panel is open
  const [multi,         setMulti]         = useState(false);   // "Select several" mode
  const [lastLine,      setLastLine]      = useState(() => readPref('chart2LastLine', true));
  const [studies,       setStudies]       = useState([]);      // indicators on the chart
  const [hoverIdx,      setHoverIdx]      = useState(null);
  const [paneLayout,    setPaneLayout]    = useState([]);      // [{key, top, h}] of indicator panes
  const [showPicker,    setShowPicker]    = useState(false);
  const [studyFor,      setStudyFor]      = useState(null);    // indicator whose settings are open
  const [prefs,         setPrefs]         = useState({ favourites: [], layout: null });
  const [alertFor,      setAlertFor]      = useState(null);    // alert line whose settings are open
  const [info,          setInfo]          = useState(null);    // /chart/info for the company strip
  const [analyst,       setAnalyst]       = useState(null);    // /chart/analyst
  const [showAnalyst,   setShowAnalyst]   = useState(() => readPref('chart2Analyst', false));
  const [showList,      setShowList]      = useState(false);
  const [showPatterns,  setShowPatterns]  = useState(false);
  const [patternSettings, setPatternSettings] = useState(() => readPatternSettings(user?.id || 'guest'));
  const [patternsLoaded, setPatternsLoaded] = useState(false);
  const [showMyAlerts,  setShowMyAlerts]  = useState(false);
  const [toolState,     setToolState]     = useState({ type: null, step: 0 });
  const [autoSave,      setAutoSave]      = useState(() => readPref('chart2AutoSave', true));
  const [magnet,        setMagnet]        = useState(() => readPref('chart2Magnet', window.matchMedia('(pointer: coarse)').matches));
  const [dirty,         setDirty]         = useState(false);
  const [saveStatus,    setSaveStatus]    = useState(null);   // {text, color}
  const [futOpts,       setFutOpts]       = useState(() => ({ adj: readPref('chart2FutAdj', false),
                                                                settle: readPref('chart2FutSettle', true),
                                                                ext: readPref('chart2FutExt', false) }));
  const [unit,          setUnit]          = useState('');
  const [liveLabel,     setLiveLabel]     = useState(null);
  const [sets,          setSets]          = useState([]);      // annotation sets for this chart (own + shared)
  const [activeSet,     setActiveSet]     = useState(null);    // own set new drawings go into
  const [visibleSets,   setVisibleSets]   = useState(() => new Set());

  const pageRef     = useRef(null);
  const navRef      = useRef(null);
  const patternTouched = useRef(false);
  const hostRef     = useRef(null);
  const pointerRef  = useRef(null);       // mouse position within the chart canvas
  const menuRef     = useRef(null);
  const dailyRef    = useRef({ bars: [], vol: false });   // daily candles; other timeframes are built from these
  const tfRef       = useRef(tf);
  const extRef      = useRef(true);
  const scopeRef    = useRef(scopeOf(tf));  // 'daily' | 'intraday': which drawings the editor holds
  const intraRef    = useRef({ t: null, bars: null, req: null });   // 1-minute candles for the ticker
  // Every drawing of the chart's sets (both scopes) + own alert lines; the editor holds the active
  // set's drawings for the current scope (and, daily, the alert lines) - syncEditor copies them back.
  const storeRef    = useRef({ bySet: {}, alerts: [], pending: [], loaded: false });
  const setsRef     = useRef(sets);
  setsRef.current   = sets;
  const activeRef   = useRef(null);
  const visibleRef  = useRef(new Set());
  const futRef      = useRef(futOpts);
  futRef.current = futOpts;
  const engineRef   = useRef(null);
  const auditRef    = useRef(auditMark);
  auditRef.current = auditMark;
  const searchTimer = useRef(null);
  const editorRef   = useRef(null);
  const chartTicker = useRef(null);      // ticker whose drawings are loaded
  const liveRef     = useRef({ t: null, server: null, asx: null, asxAt: 0, asxTry: 0 });
  const instKey     = useRef(Math.random());
  const versionRef  = useRef(0);         // bumps on every drawing change
  const saveTimer   = useRef(null);
  const autoSaveRef = useRef(autoSave);
  const dirtyRef    = useRef(false);
  const abortRef    = useRef(null);
  const prefsRef    = useRef(prefs);
  prefsRef.current  = prefs;
  const stylesTimer = useRef(null);
  const prefsLoaded = useRef(false);      // never PUT over prefs we haven't read yet
  const stylesDirty = useRef(false);
  const saveStyles  = () => {             // also run on leaving the page to flush a pending save
    clearTimeout(stylesTimer.current); stylesTimer.current = null;
    if (!prefsLoaded.current || !stylesDirty.current) return;
    stylesDirty.current = false;
    api.put('/users/chart-prefs', { prefs: prefsRef.current }).catch(() => {});
  };

  useEffect(() => {
    const eng = new ChartEngine(hostRef.current, {
      onHover: (bar, idx) => { setHovered(bar); setHoverIdx(idx); },
      onLayout: setPaneLayout,
      onView: () => navRef.current?.draw(),
    });
    engineRef.current = eng;
    editorRef.current = new DrawingEditor(eng, {
      onChange: () => {
        versionRef.current++;
        dirtyRef.current = true; setDirty(true);
        clearTimeout(saveTimer.current);
        if (autoSaveRef.current) saveTimer.current = setTimeout(() => saveDrawings(), AUTO_SAVE_DELAY);
      },
      onSelect: list => {
        setSelItems([...list]);
        setFibFor(f => (f && list.length === 1 && list[0] === f ? f : null));
        // Alert settings open only from the drawing bar's bell; selection changes can close them.
        setAlertFor(current => (list.length === 1 && list[0] === current ? current : null));
      },
      requestText: (initial, done) => setTextReq({ initial, done }),
      onToolChange: (type, step) => setToolState({ type, step }),
      onStyles: drawStyles => {     // debounced: sliders fire on every step
        const next = { ...prefsRef.current, drawStyles: { ...drawStyles } };
        prefsRef.current = next; setPrefs(next); stylesDirty.current = true;
        clearTimeout(stylesTimer.current);
        stylesTimer.current = setTimeout(saveStyles, 1500);
      },
    });
    editorRef.current.magnet = magnet;
    return () => {
      clearTimeout(saveTimer.current); saveStyles(); eng.destroy(); engineRef.current = null;
      if (activeChart === instKey.current) activeChart = null;
    };
  }, []); // eslint-disable-line

  useEffect(() => { if (editorRef.current) editorRef.current.magnet = magnet; writePref('chart2Magnet', magnet); }, [magnet]);
  useEffect(() => { if (editorRef.current) editorRef.current.multi = multi; }, [multi]);
  useEffect(() => {
    const eng = engineRef.current;
    if (eng) { eng.showLast = lastLine; eng.draw(); }
    writePref('chart2LastLine', lastLine);
  }, [lastLine]);
  useEffect(() => { autoSaveRef.current = autoSave; writePref('chart2AutoSave', autoSave); }, [autoSave]);

  // Keyboard shortcuts affect only the active chart, never text-editing controls.
  useEffect(() => {
    const onKey = e => {
      const ed = editorRef.current;
      if (!ed || activeChart !== instKey.current || e.defaultPrevented ||
          e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      const modifier = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (modifier && !e.altKey && !e.shiftKey) {
        if (key === 'c' && ed.sel.length) {
          if (ed.copySelected()) { e.preventDefault(); flash('Drawing copied', 'var(--success)'); }
        } else if (key === 'v' && pointerRef.current && hasDrawingClipboard()) {
          if (ed.pasteAt(pointerRef.current)) { e.preventDefault(); flash('Drawing pasted', 'var(--success)'); }
        } else if (key === 'd' && ed.sel.length) {
          if (ed.duplicateSelected()) { e.preventDefault(); flash('Drawing duplicated', 'var(--success)'); }
        }
        return;
      }
      if (e.key === 'Escape') {
        setContextMenu(null);
        if (ed.tool) ed.cancelTool(); else ed.select([]);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && ed.sel.length) {
        e.preventDefault(); ed.deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!contextMenu) return;
    const close = e => { if (!menuRef.current?.contains(e.target)) setContextMenu(null); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [contextMenu]);

  // Leaving the page with unsaved drawings (auto-save off) asks first.
  useEffect(() => {
    const onLeave = e => { if (dirtyRef.current && !autoSaveRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, []);

  const flash = (text, color, clear = true) => {
    setSaveStatus({ text, color });
    if (clear) setTimeout(() => setSaveStatus(s => (s?.text === text ? null : s)), 2500);
  };

  // ── Annotation sets ───────────────────────────────────────────────────────
  // Editor -> store: the active set's drawings for the current scope (+ alert lines on daily).
  const syncEditor = () => {
    const ed = editorRef.current, st = storeRef.current, sc = scopeRef.current;
    if (!ed || !chartTicker.current) return;
    const mine = ed.drawings.filter(d => d.type !== 'alert');
    for (const d of mine) d.scope = sc;
    const act = activeRef.current;
    if (act != null) st.bySet[act] = [...(st.bySet[act] || []).filter(d => (d.scope || 'daily') !== sc), ...mine];
    else st.pending = [...st.pending.filter(d => (d.scope || 'daily') !== sc), ...mine];
    if (sc === 'daily') st.alerts = ed.drawings.filter(d => d.type === 'alert');
  };

  // Store -> editor: active set (current scope) editable, other shown sets as read-only overlay.
  const applyScope = () => {
    const ed = editorRef.current, st = storeRef.current, sc = scopeRef.current, act = activeRef.current;
    if (!ed) return;
    const inScope = d => (d.scope || 'daily') === sc;
    const mine = (act != null ? st.bySet[act] || [] : st.pending).filter(inScope);
    ed.setDrawings([...mine, ...(sc === 'daily' ? st.alerts : [])]);
    ed.setOverlay([...visibleRef.current].filter(id => id !== act)
      .flatMap(id => (st.bySet[id] || []).filter(inScope).map(d => ({ ...d, locked: true }))));
  };

  const setActive = (id, vis = visibleRef.current) => {
    activeRef.current = id; setActiveSet(id);
    const v = new Set(vis); if (id != null) v.add(id);
    visibleRef.current = v; setVisibleSets(v);
    writeSetPref(user?.id, chartTicker.current, { active: id, visible: [...v] });
  };

  // Saves first when there are unsaved drawings (only the active set is ever sent).
  const flushThen = async fn => { if (dirtyRef.current) await saveDrawings(); return fn(); };

  // Sends the active set's full drawing list (both scopes) and the user's alert lines; new rows get ids.
  const saveDrawings = async () => {
    const t = chartTicker.current, st = storeRef.current;
    if (!editorRef.current || !t || !st.loaded) return;
    clearTimeout(saveTimer.current);
    syncEditor();
    const version = versionRef.current;
    flash('Saving…', 'var(--warning)', false);
    try {
      let act = activeRef.current;
      if (act == null && st.pending.length) {        // first drawing on a chart with no set of your own
        const r = await api.post('/drawings/sets/create', { ticker: t, name: 'My drawings' });
        act = r.data.set.id;
        st.bySet[act] = st.pending; st.pending = [];
        setSets(list => [...list.filter(x => x.own), r.data.set, ...list.filter(x => !x.own)]);
        setActive(act);
      }
      const posts = [[null, st.alerts]];
      if (act != null) posts.push([act, st.bySet[act] || []]);
      for (const [setId, list] of posts) {
        const r = await api.post('/drawings/save', { ticker: t, set_id: setId, drawings: list.map(serialise) });
        for (const d of list) if (!d.id && r.data.ids?.[d.key]) d.id = r.data.ids[d.key];
      }
      if (versionRef.current === version && chartTicker.current === t) { dirtyRef.current = false; setDirty(false); }
      flash('Saved', 'var(--success)');
    } catch (_) {
      flash('Save failed', 'var(--error)');
    }
  };

  const loadDrawings = async (t) => {
    chartTicker.current = t;
    dirtyRef.current = false; setDirty(false);
    const st = { bySet: {}, alerts: [], pending: [], loaded: false };
    storeRef.current = st;
    let list = [];
    try {
      const r = await api.get(`/drawings?ticker=${encodeURIComponent(t)}`);
      if (chartTicker.current !== t) return;
      for (const row of r.data.drawings || []) {
        const d = { id: row.id, key: `s${row.id}`, type: row.type, ...row.data, state: row.state, scope: row.scope || 'daily' };
        if (!TOOLS[d.type] || !d.pts?.length) continue;
        if (row.set_id == null) st.alerts.push(d); else (st.bySet[row.set_id] = st.bySet[row.set_id] || []).push(d);
      }
      list = r.data.sets || [];
      st.loaded = true;
    } catch (_) {}
    if (chartTicker.current !== t) return;
    const own = list.filter(x => x.own), ids = new Set(list.map(x => x.id)), saved = readSetPref(user?.id, t);
    const act = saved && own.some(x => x.id === saved.active) ? saved.active : own[0]?.id ?? null;
    setSets(list);
    setActive(act, new Set((saved?.visible || []).filter(id => ids.has(id))));
    applyScope();
  };

  const setOp = async (fn, ok) => {
    try { await fn(); if (ok) flash(ok, 'var(--success)'); }
    catch (e) { flash(e.response?.data?.error || 'Could not update the set', 'var(--error)'); }
  };
  const patchSet = (id, patch) => setSets(list => list.map(x => (x.id === id ? { ...x, ...patch } : x)));
  const SetHandlers = {
    onToggle: id => {
      if (id === activeRef.current) return flash('This is the set you are drawing in - make another active first', 'var(--text-muted)');
      const v = new Set(visibleRef.current);
      if (v.has(id)) v.delete(id); else v.add(id);
      visibleRef.current = v; setVisibleSets(v);
      writeSetPref(user?.id, chartTicker.current, { active: activeRef.current, visible: [...v] });
      applyScope();
    },
    onActivate: id => flushThen(() => { syncEditor(); setActive(id); applyScope(); }),
    onNew: name => flushThen(() => setOp(async () => {
      const r = await api.post('/drawings/sets/create', { ticker: chartTicker.current, name });
      syncEditor();
      storeRef.current.bySet[r.data.set.id] = [];
      setSets(list => [...list.filter(x => x.own), r.data.set, ...list.filter(x => !x.own)]);
      setActive(r.data.set.id); applyScope();
    }, 'Set created')),
    onCopy: src => flushThen(() => setOp(async () => {
      const r = await api.post('/drawings/sets/create', { ticker: chartTicker.current, name: `${src.name} (${src.owner})`, copy_from: src.id });
      syncEditor();
      storeRef.current.bySet[r.data.set.id] = (r.data.drawings || []).map(row => (
        { id: row.id, key: `s${row.id}`, type: row.type, ...row.data, scope: row.scope || 'daily' }));
      setSets(list => [...list.filter(x => x.own), r.data.set, ...list.filter(x => !x.own)]);
      const v = new Set(visibleRef.current); v.delete(src.id);
      setActive(r.data.set.id, v); applyScope();
    }, 'Copied - you can edit your copy')),
    onRename: (x, name) => setOp(async () => { await api.post('/drawings/sets/update', { id: x.id, name }); patchSet(x.id, { name }); }),
    onPrivacy: (x, privacy) => setOp(async () => {
      await api.post('/drawings/sets/update', { id: x.id, privacy });
      patchSet(x.id, { privacy, ...(privacy !== 'Share' ? { shares: [] } : {}) });
    }),
    onShare: (x, username) => setOp(async () => {
      await api.post('/drawings/sets/share', { id: x.id, username });
      patchSet(x.id, { privacy: 'Share', shares: [...new Set([...(x.shares || []), username])].sort() });
    }, `Shared with ${username}`),
    onUnshare: (x, username) => setOp(async () => {
      await api.post('/drawings/sets/unshare', { id: x.id, username });
      patchSet(x.id, { shares: (x.shares || []).filter(u => u !== username) });
    }),
    onDelete: x => setOp(async () => {
      await api.post('/drawings/sets/delete', { id: x.id });
      delete storeRef.current.bySet[x.id];
      const rest = setsRef.current.filter(y => y.id !== x.id);
      setSets(rest);
      const v = new Set(visibleRef.current); v.delete(x.id);
      if (activeRef.current === x.id) {
        dirtyRef.current = false; setDirty(false);
        setActive(rest.find(y => y.own)?.id ?? null, v);
      } else { visibleRef.current = v; setVisibleSets(v); }
      applyScope();
    }, 'Set deleted'),
  };

  // Phone vs desktop layout follows the same 768px breakpoint as the CSS.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Browser full screen where supported; otherwise (e.g. iPhone) a CSS-only full view.
  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);

  const toggleFull = () => {
    if (document.fullscreenElement) return document.exitFullscreen();
    const el = pageRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => setFull(f => !f));
    else setFull(f => !f);
  };

  // Workspace audit rays are temporary and never enter the saved drawing collection.
  useEffect(() => {
    const eng = engineRef.current;
    if (!eng) return;
    const alerts = !auditMark ? [] : Array.isArray(auditMark) ? auditMark : [auditMark];
    eng.auditMarks = alerts.map((a, i) => ({
      d: a.day, price: a.price, direction: a.direction, number: a.number || i + 1,
    }));
    if (alerts.length && eng.bars.length) eng.focusAudit(alerts[0].day);
    else eng.draw();
  }, [auditMark]);

  // Pre-market candles only show on the daily timeframe (they'd skew a week or month).
  const shownBars = (id, src) => aggregate(id === '1D' ? src.bars : src.bars.filter(b => b.live !== 'pre'), id);
  const showTimeframe = (id, src = dailyRef.current) => {
    const eng = engineRef.current;
    if (isIntraday(id)) return showIntraday(id);
    if (eng) eng.intraday = null;
    const bars = shownBars(id, src);
    setLastBar(bars[bars.length - 1] || null);
    eng?.setData(bars, src.vol, src.bars);
    const firstAudit = Array.isArray(auditRef.current) ? auditRef.current[0] : auditRef.current;
    if (firstAudit) engineRef.current?.focusAudit(firstAudit.day);
  };

  // ── Intraday (1m-4h) ─────────────────────────────────────────────────────────
  // 1-minute candles for the ticker (cached), each with its New York day / minute / session.
  const loadIntraday = t => {
    const I = intraRef.current;
    if (I.t === t && (I.bars || I.req)) return I.bars ? Promise.resolve(I.bars) : I.req;
    const hours = {};      // New York offsets are whole hours: one lookup per hour of data
    const req = api.get(`/chart/intraday?ticker=${encodeURIComponent(t)}`).then(r => {
      const f = r.data.adj_factor || [];
      const bars = r.data.ohlcv.map((x, i) => {
        const hk = Math.floor(x[0] / 3600000), P = hours[hk] || (hours[hk] = nyParts(hk * 3600000));
        const min = P.min + Math.floor((x[0] % 3600000) / 60000);
        return { t: x[0], o: x[1], h: x[2], l: x[3], c: x[4], v: x[5] || 0, f: f[i] || 1,
                 d: new Date(x[0]).toISOString().slice(0, 16) + 'Z',     // drawing anchor (UTC minute)
                 day: `${P.y}-${pad2(P.mo)}-${pad2(P.day)}`, dd: P.day, mo: P.mo, wd: P.wd, min, sess: nySession(min) };
      });
      if (intraRef.current.t === t) intraRef.current = { t, bars, req: null };
      return bars;
    });
    intraRef.current = { t, bars: null, req };
    req.catch(() => { if (intraRef.current.req === req) intraRef.current = { t: null, bars: null, req: null }; });
    return req;
  };

  const showIntraday = id => {
    const t = chartTicker.current || ticker;
    setLoading(true);
    return loadIntraday(t).then(all => {
      const eng = engineRef.current;
      if (tfRef.current !== id || !eng) return;
      const base = extRef.current ? all : all.filter(b => b.sess === 'reg');
      eng.intraday = { barMin: tfInfo(id).min };
      const bars = aggregate(base, id);
      setLastBar(bars[bars.length - 1] || null);
      eng.setData(bars, true, base);
    }).catch(e => {
      flash(e.response?.data?.error || 'Could not load intraday candles', 'var(--error)');
      if (tfRef.current === id) changeTf('1D');
    }).finally(() => setLoading(false));
  };

  // ── Live candle (today so far, until the EOD bar arrives) ────────────────────
  const applyLive = () => {
    const src = dailyRef.current, eng = engineRef.current, L = liveRef.current;
    if (!src.base) return;
    const lastD = src.base.length ? src.base[src.base.length - 1].d : null, s = L.server?.live;
    const ok = b => b && [b.o, b.h, b.l, b.c].every(v => typeof v === 'number');
    let bar = null, label = null, post = null;
    if (L.asx && (!lastD || L.asx.d > lastD) && ok(L.asx)) {            // browser-fetched ASX: freshest
      bar = { ...L.asx, live: 'reg' };
      label = mktState(s && s.d === L.asx.d ? s.session : null);
    } else if (s && (!lastD || s.d > lastD)) {
      if (s.session === 'pre' && ok(s.pre)) {
        bar = { ...s.pre, d: s.d, live: 'pre' };
        label = 'PRE';
      } else if (ok(s.candle)) {
        bar = { ...s.candle, d: s.d, live: 'reg' };
        label = mktState(s.session);
        if (s.post?.c) post = { p: s.post.c };
      }
    }
    src.bars = bar ? [...src.base, { t: Date.parse(bar.d + 'T00:00:00Z'), o: bar.o, h: bar.h, l: bar.l, c: bar.c,
                                     v: src.vol ? bar.v || 0 : 0, d: bar.d, f: 1, live: bar.live }] : src.base;
    setLiveLabel(label);
    if (!eng || isIntraday(tfRef.current)) return;      // intraday candles come from the nightly 1-minute run
    eng.postMark = post;
    const shown = shownBars(tfRef.current, src);
    setLastBar(shown[shown.length - 1] || null);
    eng.updateData(shown, src.vol, src.bars);
  };

  const liveTick = async (t, wantAsx) => {
    if (!t || t.includes('/') || isFutTicker(t)) return;
    try {
      const r = await api.get(`/chart/live?ticker=${encodeURIComponent(t)}`);
      if (liveRef.current.t !== t) return;
      liveRef.current.server = r.data;
      if (wantAsx && r.data.asx) {
        liveRef.current.asxTry = Date.now();
        try {
          const a = await asxToday(r.data.asx);
          if (liveRef.current.t === t && a) { liveRef.current.asx = a; liveRef.current.asxAt = Date.now(); }
        } catch (_) { /* the server's 20-minute data stays in use */ }
      }
      if (liveRef.current.t === t) applyLive();
    } catch (_) { /* no live candle - the chart is unaffected */ }
  };

  useEffect(() => {
    const id = setInterval(() => {
      const L = liveRef.current;
      if (!L.t || document.visibilityState !== 'visible') return;
      const focused = document.hasFocus() && (!embedded || activeChart === instKey.current);
      liveTick(L.t, focused && Date.now() - L.asxTry >= ASX_EVERY);
    }, LIVE_EVERY);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const changeTf = (id) => {
    const sc = scopeOf(id), switching = sc !== scopeRef.current;
    if (sc === 'intraday' && !intradayOk(chartTicker.current || ticker)) return;
    // Swap the editor's drawings now, while it still holds the old scope's ones.
    if (switching) { syncEditor(); scopeRef.current = sc; applyScope(); }
    tfRef.current = id; setTf(id);
    const shown = showTimeframe(id);
    if (shown?.then) shown.then(() => runPatterns()); else runPatterns();
  };

  // Favourite timeframes and EXT live in the user's chart prefs (saved like the drawing styles).
  const updatePrefs = patch => {
    const next = { ...prefsRef.current, ...patch };
    prefsRef.current = next; setPrefs(next); stylesDirty.current = true;
    clearTimeout(stylesTimer.current);
    stylesTimer.current = setTimeout(saveStyles, 1500);
  };
  const tfFavourites = prefs.tfFav || DEFAULT_FAV_TF;
  const ext = prefs.tfExt !== false;
  const anStyle = React.useMemo(() => analystStyle(prefs.analystStyle), [prefs.analystStyle]);   // analyst ratings look (AnalystMenu)
  extRef.current = ext;
  const changeExt = on => { updatePrefs({ tfExt: on }); extRef.current = on; if (isIntraday(tfRef.current)) showTimeframe(tfRef.current); };

  // ── Indicators ──────────────────────────────────────────────────────────────
  const applyStudies = list => { engineRef.current?.setStudies(list); setStudies(list); };

  useEffect(() => {
    api.get('/users/chart-prefs').then(r => {
      const p = { favourites: [], layout: null, ...(r.data.prefs || {}) }, ed = editorRef.current;
      if (ed) {   // settings changed before the prefs arrived win over the stored ones
        ed.styles = { ...(p.drawStyles || {}), ...(stylesDirty.current ? ed.styles : {}) };
        p.drawStyles = ed.styles;
      }
      prefsRef.current = p; setPrefs(p); prefsLoaded.current = true;
      if (stylesDirty.current) saveStyles();
      const layout = (p.layout || []).filter(x => INDICATORS[x.id]);
      if (layout.length) applyStudies(layout.map((x, i) => ({ ...newStudy(x.id, x.params, x.styles), key: x.key || `layout:${i}`, fillOpacity: x.fillOpacity ?? 0.08, visible: x.visible !== false })));
    }).catch(() => {});
  }, []); // eslint-disable-line

  const addStudy = id => {
    const s = newStudy(id), def = INDICATORS[id];
    // A second copy of the same indicator gets the next colour so the lines can be told apart.
    const PALETTE = ['#2563EB', '#F59E0B', '#7C3AED', '#16A34A', '#DC2626', '#0891B2', '#DB2777'];
    const copies = studies.filter(x => x.id === id).length;
    if (copies && def.outputs.length === 1 && def.outputs[0].kind === 'line') {
      const k = def.outputs[0].k, i = PALETTE.indexOf(def.outputs[0].color);
      s.styles[k] = { ...s.styles[k], color: PALETTE[(Math.max(i, 0) + copies) % PALETTE.length] };
    }
    applyStudies([...studies, s]);
    setShowPicker(false);
  };

  const updateStudy = (s, patch) => {
    Object.assign(s, patch);
    applyStudies([...studies]);
    if (studyFor === s) setStudyFor(s);
  };

  const removeStudy = s => { applyStudies(studies.filter(x => x !== s)); if (studyFor === s) setStudyFor(null); };

  const saveDefaultLayout = async () => {
    const layout = studies.map(s => ({ key: s.key, id: s.id, params: s.params, styles: s.styles, fillOpacity: s.fillOpacity, visible: s.visible }));
    const next = { ...prefsRef.current, layout };
    try { await api.put('/users/chart-prefs', { prefs: next }); setPrefs(next); flash('Default layout saved', 'var(--success)'); }
    catch (_) { flash('Could not save layout', 'var(--error)'); }
  };

  const onTool = (id) => {
    const eng = engineRef.current;
    if (id === 'reset') eng?.reset();
    else if (id === 'fit') eng?.fitAll();
    else if (id === 'full') toggleFull();
    else if (id === 'lastLine') setLastLine(v => !v);
    else if (id === 'ind:add') setShowPicker(true);
    else if (id === 'alert:line') {
      if (isIntraday(tfRef.current)) flash('Alert lines are drawn on daily charts (D to Y) for now', 'var(--text-muted)');
      else editorRef.current?.setTool('alert');
    }
    else if (id === 'alert:list') setShowMyAlerts(true);
    else if (id === 'ind:default') saveDefaultLayout();
    else if (id === 'ind:clear') { if (window.confirm('Remove all indicators from the chart?')) { applyStudies([]); setStudyFor(null); } }
    else if (id.startsWith('draw:')) editorRef.current?.setTool(id.slice(5));
    else if (id === 'magnet') setMagnet(m => !m);
    else if (id === 'multi') setMulti(m => !m);
    else if (id === 'clearAll') { if (window.confirm('Delete all drawings on this chart?')) editorRef.current?.deleteAll(); }
    else if (id === 'save') saveDrawings();
    else if (id === 'autosave') setAutoSave(a => { if (!a && dirtyRef.current) setTimeout(saveDrawings, 0); return !a; });
  };

  // Toolbar groups. Add a tool to a group by adding an entry here; `soon` groups are
  // placeholders that light up as later phases deliver them.
  const GROUPS = [
    { id: 'view', label: 'View', icon: RotateCcw, tools: [
      { id: 'reset', label: 'Reset view', icon: RotateCcw },
      { id: 'fit',   label: 'Fit all history', icon: ArrowLeftRight },
      { id: 'full',  label: full ? 'Exit full screen' : 'Full screen', icon: full ? Minimize : Maximize },
      { id: 'lastLine', label: `Last price line: ${lastLine ? 'On' : 'Off'}`, icon: IconHLine },
    ] },
    { id: 'draw', label: 'Draw', icon: Pencil, tools: [
      ...DRAW_TOOLS.map(([type, icon]) => ({ id: `draw:${type}`, label: TOOLS[type].label, icon })),
      { id: 'magnet',   label: `Magnet: ${magnet ? 'On' : 'Off'}`, icon: IconMagnet },
      { id: 'multi',    label: `Select several: ${multi ? 'On' : 'Off'}`, icon: IconSelect },
      { id: 'clearAll', label: 'Delete all drawings', icon: IconTrash },
    ] },
    { id: 'save', label: 'Save', icon: IconSave, tools: [
      { id: 'save',     label: dirty ? 'Save now' : 'Save (no changes)', icon: IconSave },
      { id: 'autosave', label: `Auto-save: ${autoSave ? 'On' : 'Off'}`, icon: IconAuto },
    ] },
    { id: 'alerts', label: 'Alerts', icon: Bell, tools: [
      { id: 'alert:line', label: 'Alert line', icon: Bell },
      { id: 'alert:list', label: 'My alert lines', icon: Bell },
    ] },
    { id: 'indicators', label: 'Indicators', icon: Activity, tools: [
      { id: 'ind:add',     label: 'Add indicator…', icon: Activity },
      { id: 'ind:default', label: 'Save as my default layout', icon: IconSave },
      { id: 'ind:clear',   label: 'Remove all indicators', icon: IconTrash },
    ] },
  ];

  // ── Analyst rating dates on the chart ──────────────────────────────────────
  useEffect(() => {
    const eng = engineRef.current;
    if (!eng) return;
    eng.analystStyle = anStyle;
    eng.analystMarks = showAnalyst ? (analyst?.ratings || []).map(r => ({
      d: r.date, target: r.price_target, firm: r.firm, rating: r.rating, colour: anStyle.colours[r.rating_norm] || '#7C3AED' })) : [];
    eng.draw();
    writePref('chart2Analyst', showAnalyst);
  }, [showAnalyst, analyst, anStyle]);

  // ── Auto chart patterns (same detector and per-user settings as the old chart) ──
  useEffect(() => {
    if (!user?.id) { setPatternsLoaded(true); return; }
    let cancelled = false;
    api.get('/users/chart-pattern-settings')
      .then(r => { if (!cancelled && r.data.settings && !patternTouched.current) setPatternSettings(normaliseSettings(r.data.settings)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPatternsLoaded(true); });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Detected on the candles shown (current timeframe); loadChart calls it with the latest settings via the ref.
  const patternRef = useRef(patternSettings);
  patternRef.current = patternSettings;
  const runPatterns = (settings = patternRef.current) => {
    const eng = engineRef.current;
    if (!eng) return;
    const ohlcv = eng.bars.map(b => [b.t, b.o, b.h, b.l, b.c, b.v]);
    eng.patterns = dailyRef.current.ratio || !settings.enabled || isIntraday(tfRef.current) ? [] : detectPatterns(ohlcv, settings)
      .map(p => ({ ...p, name: PATTERN_TYPES.find(t => t.id === p.id)?.name || p.id }));
    eng.draw();
  };

  useEffect(() => {
    runPatterns(patternSettings);
    try { localStorage.setItem('chartPatterns:v1:' + (user?.id || 'guest'), JSON.stringify(patternSettings)); } catch (_) {}
    if (!patternsLoaded || !user?.id) return;
    const t = setTimeout(() => api.put('/users/chart-pattern-settings', { settings: patternSettings }).catch(() => {}), 600);
    return () => clearTimeout(t);
  }, [patternSettings, patternsLoaded]); // eslint-disable-line

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadChart = useCallback(async (t) => {
    if (!t) return;
    setLoading(true); setError('');
    setInfo(null); setAnalyst(null); setUnit(''); setLiveLabel(null);
    liveRef.current = { t, server: null, asx: null, asxAt: 0, asxTry: 0 };
    if (engineRef.current) engineRef.current.postMark = null;
    const fut = isFutTicker(t);
    const fq = fut ? `&adj=${futRef.current.adj ? 1 : 0}&settle=${futRef.current.settle ? 1 : 0}&ext=${futRef.current.ext ? 1 : 0}` : '';
    if (!t.includes('/') && !fut) {
      api.get(`/chart/info?ticker=${encodeURIComponent(t)}`).then(r => setInfo(r.data)).catch(() => {});
      api.get(`/chart/analyst?ticker=${encodeURIComponent(t)}`).then(r => setAnalyst(r.data)).catch(() => {});
    }
    try {
      const isRatio = t.includes('/');
      let ohlcv, title, adjf = null;
      if (isRatio) {
        const [t1, t2] = t.split('/');
        const r = await api.get(`/chart/ratio?t1=${encodeURIComponent(t1)}&t2=${encodeURIComponent(t2)}${fq}`);
        ohlcv = r.data.ohlcv; title = r.data.pair;
      } else {
        const r = await api.get(`/chart/data?ticker=${encodeURIComponent(t)}${fq}`);
        ohlcv = r.data.ohlcv; title = r.data.name || t; adjf = r.data.adj_factor;
        if (r.data.contracts) {                 // futures: name the contract on the last candle
          title += ` · ${r.data.contracts[r.data.contracts.length - 1]}`;
          setUnit(r.data.unit || '');
        }
      }
      const vol = !isRatio && ohlcv[0]?.length > 5;
      // d = candle date, f = adj_factor (split-adjusted / as-traded) - drawings anchor to these.
      const bars = ohlcv.map((r, i) => ({ t: r[0], o: r[1], h: r[2], l: r[3], c: r[4], v: vol ? r[5] || 0 : 0,
                                          d: new Date(r[0]).toISOString().slice(0, 10), f: adjf?.[i] || 1 }));
      dailyRef.current = { bars, base: bars, vol, ratio: isRatio };
      setName(title); setHasVol(vol);
      intraRef.current = { t: null, bars: null, req: null };
      chartTicker.current = t;
      if (isIntraday(tfRef.current) && !intradayOk(t)) { tfRef.current = '1D'; setTf('1D'); scopeRef.current = 'daily'; }
      showTimeframe(tfRef.current, dailyRef.current);
      runPatterns();
      loadDrawings(t);
      liveTick(t, true);
    } catch (e) {
      dailyRef.current = { bars: [], vol: false };
      setError(e.response?.data?.error || 'Could not load chart data');
      setLastBar(null);
      engineRef.current?.setData([], false);
      chartTicker.current = null;
      editorRef.current?.setDrawings([]);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadChart(ticker); }, []); // eslint-disable-line

  // Embedded (Workspace / Scans): the parent switches ticker or asks for a refresh.
  useEffect(() => {
    if (embedded && initialTicker && initialTicker !== ticker) { setTicker(initialTicker); setInputVal(initialTicker); loadChart(initialTicker); }
  }, [initialTicker]); // eslint-disable-line
  useEffect(() => { if (refreshKey > 0) loadChart(ticker); }, [refreshKey]); // eslint-disable-line

  const openTicker = (t) => {
    if (dirtyRef.current) {
      if (autoSaveRef.current) saveDrawings();   // flush the pending auto-save for the old ticker
      else if (!window.confirm('You have unsaved drawings on this chart. Discard them?')) return;
    }
    setInputVal(t); setTicker(t); setShowDropdown(false);
    loadChart(t);
    if (onTickerChange) onTickerChange(t);
  };

  // ── Search (same behaviour as Chart.js) ───────────────────────────────────
  const doSearch = useCallback((q) => {
    if (abortRef.current) abortRef.current.abort();
    if (!q) { setSearchResults([]); setShowDropdown(false); return; }
    abortRef.current = new AbortController();
    api.get(`/chart/search?q=${encodeURIComponent(q)}&indexes=1`, { signal: abortRef.current.signal })
      .then(r => { setSearchResults(r.data); setShowDropdown(r.data.length > 0); })
      .catch(() => {});
  }, []);

  const canvasPos = e => {
    const rect = engineRef.current?.over.getBoundingClientRect();
    return rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : null;
  };
  const copyAnnotations = () => {
    const n = editorRef.current?.copySelected() || 0;
    flash(n ? `Copied ${n} drawing${n === 1 ? '' : 's'}` : 'Price alerts cannot be copied',
          n ? 'var(--success)' : 'var(--text-muted)');
  };
  const pasteAnnotations = at => {
    const n = editorRef.current?.pasteAt(at) || 0;
    flash(n ? `Pasted ${n} drawing${n === 1 ? '' : 's'}` : 'Cannot paste at this position',
          n ? 'var(--success)' : 'var(--text-muted)');
  };
  const duplicateAnnotations = () => {
    const n = editorRef.current?.duplicateSelected() || 0;
    flash(n ? `Duplicated ${n} drawing${n === 1 ? '' : 's'}` : 'Cannot duplicate this selection',
          n ? 'var(--success)' : 'var(--text-muted)');
  };
  const drawingContextMenu = e => {
    const eng = engineRef.current, ed = editorRef.current;
    if (!eng || !ed || ed.tool || e.target !== eng.over) return;
    const p = canvasPos(e);
    if (!p || ed.paneAtPoint(p) === undefined) return;
    const hit = ed.hitTest(p, 6);
    if (!hit && !hasDrawingClipboard()) return;
    e.preventDefault();
    activeChart = instKey.current;
    pointerRef.current = p;
    if (hit && !ed.sel.includes(hit)) ed.select([hit]);
    setContextMenu({ x: Math.max(4, Math.min(p.x, eng.w - 170)),
                     y: Math.max(4, Math.min(p.y, eng.h - 170)),
                     at: p, selected: !!hit });
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.includes('/')) { setShowDropdown(false); return; }
    searchTimer.current = setTimeout(() => doSearch(v), SEARCH_DEBOUNCE);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTimer.current) clearTimeout(searchTimer.current);
      if (abortRef.current) abortRef.current.abort();
      const val = inputVal.trim().toUpperCase();
      if (val) openTicker(val);
    }
    if (e.key === 'Escape') setShowDropdown(false);
  };

  const isRatio = ticker.includes('/');
  const lastIdx = Math.max(0, (engineRef.current?.bars.length || 1) - 1);   // legend values when not hovering

  const badgeColor = (exch) => exch === 'AU'
    ? { background: 'var(--info-dim)', color: 'var(--info)' }
    : { background: 'var(--warning-dim)', color: 'var(--warning)' };

  return (
    <>
      <style>{CSS}</style>
      <div ref={pageRef} className={embedded ? undefined : 'c2-page'} style={embedded ? { ...S.page, height: '100%' } : S.page}
           onPointerDown={() => { activeChart = instKey.current; }} onMouseEnter={() => { activeChart = instKey.current; }}>
        {!full && !embedded && <Navbar />}

        <div className="c2-controls" style={S.controls}>
          <div className="c2-search" style={S.searchWrap}>
            <input
              style={{ ...S.searchInput, borderColor: showDropdown ? 'var(--accent)' : 'var(--border)', ...(liveLabel ? { paddingLeft: '72px' } : {}) }}
              value={inputVal}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="BHP:AU  AAPL:US  GC1!:COMEX  NST:AU/GC1!:COMEX"
              spellCheck={false}
              autoCapitalize="characters"
            />
            {liveLabel && (
              <a href="/help#live-candles" target="_blank" rel="noopener noreferrer" style={S.liveTag} title="Market state - what is the purple candle?">{liveLabel}</a>
            )}
            {showDropdown && searchResults.length > 0 && (
              <div style={S.dropdown}>
                {searchResults.map((r, i) => (
                  <div key={i} style={S.dropItem} onMouseDown={() => openTicker(`${r.symbol}:${r.exchange}`)}>
                    <span style={S.dropSymbol}>{r.symbol}</span>
                    <span style={S.dropName}>{r.name}</span>
                    <span style={{ ...S.badge, ...badgeColor(r.exchange) }}>{r.exchange}</span>
                    {['ETF', 'etf'].includes(r.asset_type) && <span style={{ ...S.badge, background: 'var(--accent-dim)', color: 'var(--accent)' }}>ETF</span>}
                    {r.asset_type === 'future' && <span style={{ ...S.badge, background: 'var(--success-dim)', color: 'var(--success)' }}>FUT</span>}
                    {r.asset_type === 'index' && <span style={{ ...S.badge, background: 'var(--warning-dim)', color: 'var(--warning)' }}>IDX</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="c2-tfbar" style={S.tfBar}>
            <TimeframePicker tf={tf} favourites={tfFavourites} onChange={changeTf} mobile={mobile}
                             onFavourites={list => updatePrefs({ tfFav: list })}
                             intradayOk={intradayOk(ticker)} ext={ext} onExt={changeExt} />
            {ticker && !isRatio && (
              <SetsMenu sets={sets} activeId={activeSet} visible={visibleSets} mobile={mobile} {...SetHandlers} />
            )}
            {ticker && !isRatio && (
              <AnalystMenu on={showAnalyst} onToggle={() => setShowAnalyst(v => !v)} mobile={mobile}
                           style={anStyle} onStyle={(patch, reset) => updatePrefs({ analystStyle: reset ? patch : { ...anStyle, ...patch } })} />
            )}
          </div>
          {isFutTicker(ticker) && (
            <div style={S.tfWrap} title="Futures: ADJ back-adjusts across rolls, SET uses the settlement price as the daily close, EXT adds IBKR's longer (ratio-adjusted) history before ~2 years">
              {[['adj', 'ADJ'], ['settle', 'SET'], ['ext', 'EXT']].map(([k, label]) => {
                const off = k === 'ext' && !futOpts.adj;
                return (
                  <button key={k} disabled={off}
                          onClick={() => {
                            const next = { ...futOpts, [k]: !futOpts[k] };
                            setFutOpts(next); futRef.current = next;
                            writePref({ adj: 'chart2FutAdj', settle: 'chart2FutSettle', ext: 'chart2FutExt' }[k], next[k]);
                            openTicker(ticker);
                          }}
                          style={{ ...S.tfBtn, ...(off ? { opacity: 0.4, cursor: 'default' } : {}),
                                   ...(futOpts[k] && !off ? { background: 'var(--accent)', color: 'var(--on-accent)' } : {}) }}>
                    {label}
                  </button>
                );
              })}
              <a href="/help#futures" target="_blank" rel="noopener noreferrer" title="What do ADJ / SET / EXT do?"
                 style={{ ...S.tfBtn, borderLeft: '1px solid var(--border)', borderRight: 'none', textDecoration: 'none' }}>?</a>
            </div>
          )}
          {ticker && <span className="c2-name" style={S.name}>{ticker} · {name}{unit ? ` (${unit})` : ''}</span>}
        </div>

        {!full && ticker && (
          <InfoBar info={info} isRatio={isRatio} futures={isFutTicker(ticker)} ticker={ticker} mobile={mobile} analyst={analyst}
                   price={info?.snapshot?.close ?? lastBar?.c ?? null}
                   onAddList={() => setShowList(true)} onPatterns={() => setShowPatterns(true)} patternsOn={patternSettings.enabled} />
        )}

        <div className="c2-readout-bar" style={S.readoutBar}><Readout bar={hovered || lastBar} hasVol={hasVol} /></div>

        <div style={S.chartRow}>
        {!mobile && <Toolbar groups={GROUPS} mobile={false} onTool={onTool} active={toolState.type === 'alert' ? 'alerts' : toolState.type ? 'draw' : null} />}
        <div style={S.chartWrap}
             onPointerMove={e => { if (e.pointerType === 'mouse' && e.target === engineRef.current?.over) pointerRef.current = canvasPos(e); }}
             onContextMenu={drawingContextMenu}>
          <div ref={hostRef} style={S.host} />
          {contextMenu && (
            <div ref={menuRef} style={{ ...S.contextMenu, left: contextMenu.x, top: contextMenu.y }}
                 onContextMenu={e => e.preventDefault()} onPointerDown={e => e.stopPropagation()}>
              {contextMenu.selected && <>
                <button type="button" style={S.contextItem} onClick={() => { copyAnnotations(); setContextMenu(null); }}>Copy <span>Ctrl+C</span></button>
                <button type="button" style={S.contextItem} onClick={() => { duplicateAnnotations(); setContextMenu(null); }}>Duplicate <span>Ctrl+D</span></button>
              </>}
              {hasDrawingClipboard() && (
                <button type="button" style={S.contextItem} onClick={() => { pasteAnnotations(contextMenu.at); setContextMenu(null); }}>Paste <span>Ctrl+V</span></button>
              )}
              {contextMenu.selected && (
                <button type="button" style={{ ...S.contextItem, color: 'var(--error)' }}
                        onClick={() => { editorRef.current?.deleteSelected(); setContextMenu(null); }}>Delete <span>Del</span></button>
              )}
            </div>
          )}
          <div style={S.topLeft}>
            {studies.filter(s => INDICATORS[s.id].pane === 'price').map(s => (
              <StudyRow key={s.key} s={s} idx={hoverIdx ?? lastIdx} onSettings={setStudyFor}
                        onToggle={x => updateStudy(x, { visible: !x.visible })} onRemove={removeStudy} />
            ))}
            {studies.filter(s => INDICATORS[s.id].pane === 'own' && !s.visible).map(s => (
              <StudyRow key={s.key} s={s} idx={hoverIdx ?? lastIdx} onSettings={setStudyFor}
                        onToggle={x => updateStudy(x, { visible: !x.visible })} onRemove={removeStudy} />
            ))}
          </div>
          {paneLayout.map(p => {
            const s = studies.find(x => x.key === p.key);
            return s && (
              <div key={p.key} style={{ ...S.paneLeg, top: p.top + 3 }}>
                <StudyRow s={s} idx={hoverIdx ?? lastIdx} onSettings={setStudyFor}
                          onToggle={x => updateStudy(x, { visible: !x.visible })} onRemove={removeStudy} />
              </div>
            );
          })}
          {studyFor && <IndicatorSettings study={studyFor} mobile={mobile} onClose={() => setStudyFor(null)}
                                          onChange={patch => updateStudy(studyFor, patch)} />}
          <button className="c2-reset" style={S.resetBtn} title="Reset view" onClick={() => engineRef.current?.reset()}>↻</button>
          {!toolState.type && (
            <DrawingBar items={selItems} mobile={mobile}
                        onChange={patch => editorRef.current?.updateSelected(patch)}
                        onDelete={() => editorRef.current?.deleteSelected()}
                        onEditText={d => editorRef.current?.editText(d)}
                        onSettings={d => (d.type === 'alert' ? setAlertFor(d) : setFibFor(d))} />
          )}
          {(saveStatus || (dirty && !autoSave)) && (
            <div style={{ ...S.saveStatus, color: saveStatus?.color || 'var(--text-muted)' }}>{saveStatus?.text || 'Unsaved changes'}</div>
          )}
          {alertFor && !toolState.type && (
            <AlertPanel drawing={alertFor} mobile={mobile} onClose={() => setAlertFor(null)}
                        onChange={patch => editorRef.current?.updateSelected(patch)} />
          )}
          {fibFor && (
            <FibPanel drawing={fibFor} mobile={mobile} onClose={() => setFibFor(null)}
                      onChange={patch => editorRef.current?.updateSelected(patch)} />
          )}
          {multi && !toolState.type && (
            <div style={{ ...S.hint, ...(selItems.length && !mobile ? { top: '76px' } : {}) }}>
              <span>Select several: {mobile ? 'tap' : 'click'} drawings to add or remove</span>
              <button style={S.hintX} title="Done" onClick={() => setMulti(false)}>✕</button>
            </div>
          )}
          {error && <div style={S.error}>{error}</div>}
          {loading && <div style={S.overlay}><div style={S.spinner} /></div>}
        </div>
        </div>
        {!mobile && <Navigator ref={navRef} engineRef={engineRef} />}
        {showMyAlerts && <MyAlerts mobile={mobile} onClose={() => setShowMyAlerts(false)}
                                 onOpen={t => { setShowMyAlerts(false); openTicker(t); }} />}
      {showList && <AddToListModal ticker={ticker} onClose={() => setShowList(false)} />}
      {showPatterns && <ChartPatternSettings settings={patternSettings} onClose={() => setShowPatterns(false)}
                                             onChange={next => { patternTouched.current = true; setPatternSettings(next); }} />}
      {showPicker && <IndicatorPicker mobile={mobile} favourites={prefs.favourites || []} onPick={addStudy} onClose={() => setShowPicker(false)} />}
      {textReq && <TextDialog initial={textReq.initial} onDone={text => { const r = textReq; setTextReq(null); r.done(text); }} />}
      {mobile && <Toolbar groups={GROUPS} mobile onTool={onTool} active={toolState.type === 'alert' ? 'alerts' : toolState.type ? 'draw' : null} />}
      </div>
    </>
  );
}
